/**
 * Haupt-App Komponente
 *
 * Modulare, saubere Struktur mit getrennten Komponenten
 */

import React, { useState, useEffect, Fragment } from 'react';
import {
  Box,
  Card,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  alpha,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useTheme } from '@mui/material/styles';
import { useAuth } from './hooks/useAuth';
import { useI18n } from './hooks/useI18n';
import { useAppAppearance } from './theme/AppAppearanceContext';
import { isHoneycombAppearance, isHoneycombLightAppearance, honeycombSurfaceWashCss, resolvedHoneycombBoardSurface } from './theme/appAppearance';
import {
  hologramSurfaceSx,
  hologramTabsSx,
} from './theme/hologramChrome';
import { LoginForm } from './components/LoginForm';
import { TranslationTab } from './components/TranslationTab';
import { EmailCreationTab } from './components/EmailCreationTab';
import { SettingsDialog } from './components/SettingsDialog';
import { usePacificRimUiSounds } from './audio/usePacificRimUiSounds';
import { useSharedSfxPrefsReadOnly } from './hooks/useSharedSfxPrefsReadOnly';
import { useSharedAppearanceSync } from './hooks/useSharedAppearanceSync';
import { subscribeHoneycombColors } from './services/userAppearanceCloud';
import { useHoneycombLiveSurface } from './hooks/useHoneycombLiveSurface';
import { HoneycombSurfacePhoto } from './components/HoneycombSurfacePhoto';

const MAIN_TAB_KEY = 'mail-aid:mainTab.v1';
/** Amber wie Navigator `--honey-ring` */
const HONEYCOMB_AMBER = '#f0b429';

function readMainTab() {
  try {
    const raw = localStorage.getItem(MAIN_TAB_KEY);
    const n = Number(raw);
    return n === 0 || n === 1 ? n : 0;
  } catch {
    return 0;
  }
}

export default function App() {
  const { user, loading, logout, error: authError } = useAuth();
  const { t } = useI18n();
  const theme = useTheme();
  const { appearance, setAppearance, backgroundSx, viewportFrameSx, hologram, setHoneycombColorMaps, honeycombColorMaps, honeycombRingColor } =
    useAppAppearance();
  const [tab, setTab] = useState(() => readMainTab());
  const [error, setError] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const dark = theme.palette.mode === 'dark';
  const accent = theme.palette.primary.main;
  const honeycomb = isHoneycombAppearance(appearance);
  const honeycombLight = isHoneycombLightAppearance(appearance);
  const liveSurface = useHoneycombLiveSurface(
    honeycomb && user?.uid ? user.uid : undefined,
    appearance
  );
  const honeycombPhotoUrl = honeycomb ? liveSurface.url : null;
  const honeycombWash = honeycombPhotoUrl
    ? honeycombSurfaceWashCss(
        resolvedHoneycombBoardSurface(appearance, honeycombColorMaps),
        liveSurface.showThrough
      )
    : null;
  const honeycombTabAccent = honeycomb
    ? honeycombRingColor || (honeycombLight ? '#d4a017' : HONEYCOMB_AMBER)
    : theme.palette.primary.main;
  usePacificRimUiSounds(appearance);
  useSharedSfxPrefsReadOnly(user?.uid);
  const publishAppearance = useSharedAppearanceSync(user?.uid, setAppearance);
  useEffect(() => {
    if (!user?.uid) return;
    return subscribeHoneycombColors(user.uid, setHoneycombColorMaps);
  }, [user?.uid, setHoneycombColorMaps]);

  useEffect(() => {
    try {
      localStorage.setItem(MAIN_TAB_KEY, String(tab));
    } catch {
      /* ignore */
    }
  }, [tab]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1,
          ...backgroundSx,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Fragment>
        <Box sx={{ minHeight: '100vh', ...backgroundSx }}>
          <LoginForm />
        </Box>
        {authError && (
          <Box
            sx={{
              position: 'fixed',
              top: 'max(16px, env(safe-area-inset-top))',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9999,
              maxWidth: { xs: '90%', sm: 600 },
              width: '100%',
              px: 1,
            }}
          >
            <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>
              {authError.message || String(authError)}
            </Alert>
          </Box>
        )}
      </Fragment>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        display: 'flex',
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'center',
        pt: { xs: 0, sm: 1, md: 0 },
        px: { xs: 1, sm: 2, md: 0 },
        pb: { xs: 1, sm: 2, md: 0 },
        boxSizing: 'border-box',
        ...backgroundSx,
        ...(honeycomb && honeycombTabAccent
          ? { ['--honey-ring']: honeycombTabAccent }
          : {}),
      }}
    >
      {honeycombPhotoUrl && honeycombWash ? (
        <HoneycombSurfacePhoto url={honeycombPhotoUrl} wash={honeycombWash} />
      ) : null}
      {viewportFrameSx && <Box sx={viewportFrameSx} aria-hidden />}
      <Box
        sx={{
          position: 'fixed',
          top: { xs: 'max(8px, env(safe-area-inset-top))', md: 20 },
          left: { xs: 'max(8px, env(safe-area-inset-left))', md: 20 },
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: 'background.paper',
            border: dark
              ? `1px solid ${alpha(theme.palette.primary.main, 0.55)}`
              : '1px solid rgba(0,0,0,0.08)',
            boxShadow: '0 0 10px rgba(0,0,0,0.09)',
          }}
        >
          <Tooltip title={t.settings.title}>
            <IconButton
              size="small"
              onClick={() => setSettingsOpen(true)}
              aria-label={t.settings.title}
              sx={{
                color: 'text.secondary',
                minWidth: 30,
                minHeight: 30,
                width: 30,
                height: 30,
                p: 0.5,
              }}
            >
              <SettingsIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Card
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          minWidth: { xs: 0, sm: 350, md: 700 },
          maxWidth: { xs: '100%', sm: 600, md: 1200 },
          width: '100%',
          m: { xs: 0, md: 2 },
          mt: { xs: 0, md: 2 },
          position: 'relative',
          zIndex: 1,
          ...(hologram
            ? {
                ...hologramSurfaceSx(accent),
                color: 'rgba(232, 246, 255, 0.96)',
              }
            : {
                bgcolor: honeycomb
                  ? alpha(theme.palette.background.paper, honeycombLight ? 0.82 : 0.68)
                  : 'background.paper',
                borderRadius: { xs: 2, md: 3 },
                backdropFilter: honeycomb ? 'blur(14px)' : undefined,
                boxShadow: dark
                  ? honeycomb
                    ? `0 0 36px ${alpha(theme.palette.primary.main, 0.16)}, ${theme.shadows[6]}`
                    : `0 0 24px ${alpha(theme.palette.primary.main, 0.12)}, ${theme.shadows[6]}`
                  : { xs: 2, md: 6 },
                border: dark
                  ? `1px solid ${alpha(theme.palette.primary.main, honeycomb ? 0.38 : 0.35)}`
                  : 'none',
              }),
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => {
            setTab(v);
            setError('');
          }}
          centered
          sx={{
            mt: 0,
            mb: { xs: 2, md: 4 },
            '& .MuiTab-root': {
              fontSize: { xs: '0.875rem', md: '1rem' },
              minHeight: { xs: 48, md: 56 },
              padding: { xs: '8px 16px', md: '12px 24px' },
              ...(honeycomb && {
                fontWeight: 700,
                letterSpacing: '0.04em',
                '&.Mui-selected': {
                  color: honeycombTabAccent,
                },
              }),
            },
            ...(hologram ? hologramTabsSx(accent) : {}),
            ...(honeycomb && {
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: 2,
                bgcolor: honeycombTabAccent,
              },
            }),
          }}
        >
          <Tab label={t.tabs.translate} />
          <Tab label={t.tabs.create} />
        </Tabs>

        {tab === 0 && <TranslationTab />}
        {tab === 1 && <EmailCreationTab />}

        {error && (
          <Alert
            severity="error"
            sx={{ mt: 2, fontSize: { xs: '0.75rem', md: '0.875rem' } }}
          >
            {error}
          </Alert>
        )}
      </Card>

      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onLogout={logout}
        onAppearanceChange={publishAppearance}
      />
    </Box>
  );
}
